<template>
  <modal :show="show" footerClasses="d-none">
    <b-form v-loading="formProcessing" @submit.prevent="handleSubmit">
      <h4 class="mb-4">Report Access</h4>

      <ReportsCheckboxList v-model="selectedReports" @update:defaultReport="(value) => (defaultReport = value)"
        :assignee-id="member.company ? member.company.id : null" assignee-type="Corporate Admin"
        :default-report="defaultReport" :disabled="isEditingDisabled" />

      <div v-if="!isEditingDisabled" class="mt-4 d-flex justify-content-between">
        <base-button @click="() => $emit('update:show', false)" type="white" native-type="button" size="md"
          class="form-btn bg-transparent font-weight-normal px-0 shadow-none">Cancel</base-button>
        <base-button type="primary" native-type="submit" size="md"
          class="form-btn font-weight-normal">Save</base-button>
      </div>
      <div v-else class="mt-4 d-flex justify-content-end">
        <base-button @click="() => $emit('update:show', false)" type="white" native-type="button" size="md"
          class="form-btn bg-transparent font-weight-normal px-0 shadow-none">Close</base-button>
      </div>
    </b-form>
  </modal>
</template>

<script>
import Vue from "vue";
import { defineComponent, computed, ref, watch } from "@vue/composition-api";
import { createVuexHelpers } from "vue2-helpers";

import { useAuth } from "@composables/auth";
import Companies from "@services/Companies";
import Members from "@services/Members";
import Reports from "@services/Reports";

import ReportsCheckboxList from "@components/ReportsCheckboxList";

export default defineComponent({
  name: "UserReportsModal",
  components: {
    ReportsCheckboxList,
  },
  emits: ["update:show"],
  props: {
    show: Boolean,
    userId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { useGetters, useMutations } = createVuexHelpers();

    const { member } = useGetters(["member"]);
    const { UPDATE_MEMBER } = useMutations(["UPDATE_MEMBER"]);

    const { currentUser } = useAuth();

    const formProcessing = ref(false);

    const defaultReport = ref(null);
    const selectedReports = ref([]);

    const isEditingDisabled = computed(() => {
      return member.value.type === "Corporate Admin";
    });

    const assignedReportIDKey = computed(() =>
      "Employee" === member.value.type
        ? "report_assigned_member_id"
        : "report_assigned_id"
    );

    const reportsToAssignList = computed(() =>
      selectedReports.value.filter((item) => {
        return !member.value.reports.find(
          (assignedItem) => assignedItem.report_id === item
        );
      })
    );

    const reportsToUnassignList = computed(() =>
      member.value.reports
        .filter((item) => !selectedReports.value.includes(item.report_id))
        .map((item) => ({
          id: item[assignedReportIDKey.value],
          report_id: item.report_id
        }))
    );

    const getDefaultReport = async () => {
      const resp = await Members.getDefaultReport(member.value.user.id);

      defaultReport.value = resp.data.data.data.report_id;

      if (!defaultReport.value && member.value.company) {
        const resp = await Companies.readCompany(member.value.company.id);
        const defaultReportId = resp.data.data.data.default_report_id;

        defaultReport.value = defaultReportId;
      }
    };

    const handleAssignReports = async () => {
      if (!reportsToAssignList.value.length) return;

      const assignResp = await Reports.assignReports(
        reportsToAssignList.value,
        [props.userId],
        member.value.company.id
      );
      const assignedList = assignResp.data.data.data.map((item) => ({
        report_id: item.report.id,
        [assignedReportIDKey.value]: item.id,
      }));

      member.value.reports = member.value.reports.concat(assignedList);
      member.value.report_count = member.value.reports.length;
    };

    const handleUnassignReports = async () => {
      if (!reportsToUnassignList.value.length) return;

      const reportsToUnassign = [...reportsToUnassignList.value];

      const successfullyUnassigned = [];

      for await (const reportToUnassignItem of reportsToUnassign) {
        try {
          await Reports.unassignReport(reportToUnassignItem.id, member.value.type);
          successfullyUnassigned.push(reportToUnassignItem.report_id);
        } catch (error) {
          console.error('Failed to unassign report:', error);
        }
      }

      if (successfullyUnassigned.length > 0) {
        member.value.reports = member.value.reports.filter(
          (item) => !successfullyUnassigned.includes(item.report_id)
        );
        member.value.report_count = member.value.reports.length;
      }
    };

    const handleSetDefaultReport = async () => {
      if (!currentUser.value.is_superuser) return;

      const defaultReportObj = member.value.reports.find(
        (item) => item.report_id == defaultReport.value
      );

      if (defaultReportObj && defaultReportObj[assignedReportIDKey.value]) {
        await Members.setDefaultReport(
          member.value.user.id,
          defaultReportObj[assignedReportIDKey.value]
        );
      }
    };

    const handleSubmit = async () => {
      if (isEditingDisabled.value) return;

      formProcessing.value = true;

      try {
        await handleAssignReports();
        await handleUnassignReports();
        await handleSetDefaultReport();

        UPDATE_MEMBER({
          data: member.value,
        });

        emit("update:show", false);
        Vue.prototype.$toasted.show("Reports updated successfully.");
      } catch (error) {
        console.error('Error updating reports:', error);
        Vue.prototype.$toasted.error("Failed to update reports. Please try again.");
      } finally {
        formProcessing.value = false;
      }
    };

    watch(
      () => props.show,
      async () => {
        if (props.userId) {
          selectedReports.value = member.value.reports.map((item) => item.report_id);

          if (currentUser.value.is_superuser) await getDefaultReport();
        }
      }
    );

    return {
      member,
      formProcessing,
      defaultReport,
      selectedReports,
      isEditingDisabled,
      handleSubmit,
    };
  },
});
</script>